/**
 * ИграйТут SDK v1
 *
 * Глобальный объект `IgrayTut` — единственный entry point для игр.
 *
 * Использование:
 *   ```html
 *   <script src="https://igraytut.ru/sdk/v1/sdk.js"></script>
 *   <script>
 *     IgrayTut.init({ gameId: "block-siege-3d" }).then((ctx) => {
 *       if (ctx.user) console.log("Привет,", ctx.user.displayName);
 *     });
 *   </script>
 *   ```
 *
 * После init — все API доступны как `IgrayTut.ads`, `IgrayTut.storage` и т.д.
 */

import { createAchievements } from "./api/achievements";
import { createAds } from "./api/ads";
import { createAnalytics } from "./api/analytics";
import { createLeaderboard } from "./api/leaderboard";
import { createPayments } from "./api/payments";
import { createStorage } from "./api/storage";
import { isSDKErrorCode, SDK_ERROR_CODES, SDKError } from "./errors";
import { Transport } from "./transport";
import type { SdkContext, SdkInitOptions } from "./types";

declare const __SDK_VERSION__: string;

const SDK_VERSION = typeof __SDK_VERSION__ === "string" ? __SDK_VERSION__ : "0.0.0";

let transport: Transport | null = null;
let context: SdkContext | null = null;
let initialized = false;

async function init(options: SdkInitOptions): Promise<SdkContext> {
  if (initialized) {
    if (!context) throw new Error("SDK init in progress");
    return context;
  }
  initialized = true;

  if (!options?.gameId) {
    throw new Error("IgrayTut.init() requires `gameId`");
  }

  transport = new Transport(options.debug ?? false);

  // Отправляем родителю «я готов» — он может переключить GamePlayer из loading в playing
  transport.notify("sdk_ready", { gameId: options.gameId, sdkVersion: SDK_VERSION });

  // Запрашиваем контекст (user, device, locale)
  const ctx = await transport.request<SdkContext>("init", {
    gameId: options.gameId,
    sdkVersion: SDK_VERSION,
  });
  context = ctx;

  if (options.requireAuth && !ctx.user) {
    throw new Error("Auth required but user not logged in");
  }

  return ctx;
}

function getContext(): SdkContext | null {
  return context;
}

function requireTransport(): Transport {
  if (!transport) {
    throw new Error("IgrayTut.init() must be called before using SDK API");
  }
  return transport;
}

/**
 * Lazy API — создаются при первом обращении после init().
 *
 * ВАЖНО: используем `Object.defineProperties` вместо spread `{...api}` —
 * иначе spread ВЫЗЫВАЕТ геттеры в момент создания IgrayTut, а каждый геттер
 * дёргает `requireTransport()` который бросает Error т.к. init ещё не вызван.
 * Это ломало SDK целиком — даже `IgrayTut.version` становился недоступным.
 */
const apiDescriptors: PropertyDescriptorMap = {
  ads: {
    get: () => createAds(requireTransport()),
    enumerable: true,
    configurable: true,
  },
  storage: {
    get: () => createStorage(requireTransport()),
    enumerable: true,
    configurable: true,
  },
  analytics: {
    get: () => createAnalytics(requireTransport()),
    enumerable: true,
    configurable: true,
  },
  leaderboard: {
    get: () => createLeaderboard(requireTransport()),
    enumerable: true,
    configurable: true,
  },
  achievements: {
    get: () => createAchievements(requireTransport()),
    enumerable: true,
    configurable: true,
  },
  payments: {
    get: () => createPayments(requireTransport()),
    enumerable: true,
    configurable: true,
  },
};

export const IgrayTut = Object.defineProperties(
  {
    version: SDK_VERSION,
    init,
    getContext,
    // Экспорт класса ошибок и кодов — игры могут:
    //   if (e instanceof IgrayTut.SDKError && e.code === IgrayTut.ERRORS.QUOTA_EXCEEDED) ...
    SDKError,
    ERRORS: SDK_ERROR_CODES,
    isErrorCode: isSDKErrorCode,
  },
  apiDescriptors,
) as {
  version: string;
  init: typeof init;
  getContext: typeof getContext;
  SDKError: typeof SDKError;
  ERRORS: typeof SDK_ERROR_CODES;
  isErrorCode: typeof isSDKErrorCode;
  ads: ReturnType<typeof createAds>;
  storage: ReturnType<typeof createStorage>;
  analytics: ReturnType<typeof createAnalytics>;
  leaderboard: ReturnType<typeof createLeaderboard>;
  achievements: ReturnType<typeof createAchievements>;
  payments: ReturnType<typeof createPayments>;
};

// Экспортируем в глобал window — чтобы игры могли использовать без модульной сборки
declare global {
  interface Window {
    IgrayTut: typeof IgrayTut;
  }
}

if (typeof window !== "undefined") {
  window.IgrayTut = IgrayTut;
}

export default IgrayTut;
export { isSDKErrorCode, SDK_ERROR_CODES, SDKError } from "./errors";
export type { SDKErrorCode, SDKErrorOptions } from "./errors";
export type { SdkContext, SdkInitOptions, SdkUser } from "./types";
